import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { navLinks, company } from "../mock";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-end justify-between h-28 pb-3">
        {/* Logo */}
        <button
          data-testid="header-logo"
          onClick={() => scrollTo("hero")}
          className="flex flex-col items-center gap-1 group"
          aria-label={company.name}
        >
          <img
            src={company.logoMark}
            alt=""
            aria-hidden="true"
            className="h-10 md:h-12 w-auto object-contain drop-shadow-sm"
          />
          <div className="flex flex-col items-center leading-tight">
            <span
              className={`font-['Playfair_Display'] text-sm md:text-base tracking-tight whitespace-nowrap ${
                scrolled ? "text-[#0A1128]" : "text-white"
              } transition-colors duration-500`}
            >
              {company.shortName}
            </span>
            <span
              className={`text-[9px] uppercase tracking-[0.25em] whitespace-nowrap ${
                scrolled ? "text-[#475569]" : "text-white/70"
              } transition-colors duration-500`}
            >
              Business Consultants
            </span>
          </div>
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className={`text-sm tracking-wide font-medium relative group ${
                scrolled ? "text-[#0A1128]" : "text-white"
              } transition-colors duration-500`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#CFA660] group-hover:w-full transition-all duration-300" />
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => scrollTo("contact")}
            className={`text-sm font-medium px-6 py-3 border transition-all duration-300 ${
              scrolled
                ? "bg-[#0A1128] text-white border-[#0A1128] hover:bg-[#172554]"
                : "bg-transparent text-white border-white hover:bg-white hover:text-[#0A1128]"
            }`}
          >
            Schedule Consultation
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? (
            <X className={`w-6 h-6 ${scrolled ? "text-[#0A1128]" : "text-white"}`} />
          ) : (
            <Menu className={`w-6 h-6 ${scrolled ? "text-[#0A1128]" : "text-white"}`} />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-slate-200 overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="text-left text-[#0A1128] text-lg font-['Playfair_Display']"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => scrollTo("contact")}
                className="bg-[#0A1128] text-white py-4 text-sm font-medium hover:bg-[#172554] transition-colors"
              >
                Schedule Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
